/**
 * ===========================================================
 * FeatureManager Unified Helper
 * -----------------------------------------------------------
 * GUIA DE REFERENCIA RÁPIDA:
 *
 * 1. BOOLEAN FEATURES (Configuración Remota):
 *    - if (FeatureManager.isEnabled("mi-flag")) { ... }
 *    - NOTA: Si HOLDOUT_KEY está ON, siempre devuelve false.
 *
 * 2. EXPERIMENTOS (A/B Testing):
 *    - const variant = FeatureManager.getVariant("mi-test", "control");
 *    - const { value } = FeatureManager.run({
 *        key: "test-directo",
 *        variations: ["control", "variante-a"]
 *      });
 *    - // 'value' será "control" (si es Holdout o cae en control) o "variante-a"
 *    - if (value === "variante-a") { doAlpha(); }
 *
 * 3. SEGMENTACIÓN (Manual):
 *    - if (FeatureManager.isExposed()) { ... lo ven los que no son Holdout ... }
 *    - if (FeatureManager.isHoldout()) { ... solo lo ve el grupo de control ... }
 *
 * 4. REACTIVIDAD (Escucha de Cambios):
 *    - FeatureManager.onFeatureChange("flag", (enabled) => { ... });
 *      → Detecta si un boolean feature se activa/desactiva.
 *    - FeatureManager.onVariantChange("test", (variant) => { ... });
 *      → Detecta cambio de variante en un feature flag tipo A/B.
 *    - FeatureManager.onExposureChange(({ isExposed, isHoldout }) => { ... });
 *      → Detecta cuando el usuario pasa de expose ↔ holdout.
 *    - FeatureManager.onRunChange({ key: "test", variations: ["control", "v1"] }, (variant) => { ... });
 *      → Versión reactiva de run(). Detecta cambios de variante en experimentos directos.
 *
 * 5. MANIPULACIÓN DOM (Visual):
 *    - FeatureManager.toggleClass("flag", "#el", "active");
 *      → Agrega/quita clase según si el feature está activo.
 *    - FeatureManager.applyVariantClass("test", "body", "v-");
 *      → Aplica clase con prefijo según la variante (ej: "v-control", "v-v1").
 *
 * 6. HOLDOUT (Control Global):
 *    - El archivo sincroniza automáticamente el atributo 'holdOut'
 *      con el estado de la bandera padre definida en HOLDOUT_KEY.
 *
 * NOTAS TÉCNICAS:
 * - Holdout Global: Todos los checks (isEnabled, getVariant, run) pasan por el holdout.
 * - Evento Ready: Se despacha 'featureManager::ready' al inicializar el renderer.
 * - Concurrencia: Soporta múltiples features y experimentos simultáneos.
 * - Inicialización: 'waitForGrowthBook' espera a que window._growthbook esté disponible.
 * ===========================================================
 * Documentacion en: https://app.asana.com/1/22908445599079/project/1211734270790980/task/1212908672130552?focus=true
 * Basado en: https://www.notion.so/GrowthBook-FeatureManager-Helper-29364ba30d0180098c50f889eacb9e5c, gracias a Juan Martinez
 */

window.FeatureManager = (() => {
  /* Holdout configuration */
  const HOLDOUT_KEY = "holdout-pdm-remedy";
  const HOLDOUT_ATTR = "holdOut";
  const HOLDOUT_ON = "on";
  const HOLDOUT_OFF = "off";

  const featureCallbacks = new Map(); // boolean listeners
  const variantCallbacks = new Map(); // variant listeners
  const exposureCallbacks = []; // exposure (expose <-> holdout) listeners
  const runCallbacks = new Map();  // direct experiment listeners { experiment, callbacks[] }
  const lastRunState = new Map();  // last known variant per experiment key
  let lastHoldoutState = null; // tracks previous holdout state for change detection

  let localConfig = {};
  let hasRenderedOnce = false;

  /* ---------------------------------------------------------
   * GrowthBook getter
   * --------------------------------------------------------- */

  function getGrowthBook() {
    return window._growthbook;
  }

  /* ---------------------------------------------------------
   * CONFIG MANAGEMENT
   * --------------------------------------------------------- */

  function initConfig(config = {}) {
    localConfig = { ...localConfig, ...config };
  }

  function getConfig() {
    return { ...localConfig };
  }

  function getLocalValue(featureKey) {
    return localConfig.hasOwnProperty(featureKey)
      ? localConfig[featureKey]
      : undefined;
  }

  /* ---------------------------------------------------------
   * FEATURE BOOLEAN EVALUATION
   * --------------------------------------------------------- */

  function isEnabled(featureKey) {
    const gb = getGrowthBook();

    if (!gb) {
      console.warn("GrowthBook not initialized.");
      return false;
    }

    if (isHoldout()) return false;

    return gb.isOn(featureKey);
  }

  function getEffectiveFeatureState({ featureKey, defaultValue = false }) {
    const localValue = getLocalValue(featureKey);

    if (localValue === true) return true;
    if (localValue === false) return false;

    if (isEnabled(featureKey)) return true;

    return defaultValue;
  }

  /* ---------------------------------------------------------
   * MULTI-VARIANT SUPPORT
   * --------------------------------------------------------- */

  /**
   * Devuelve la variante activa de un experimento (A/B/C/etc)
   */
  function getVariant(featureKey, defaultValue = null) {
    const localValue = getLocalValue(featureKey);

    if (localValue !== undefined) {
      return localValue;
    }

    const gb = getGrowthBook();
    if (!gb) return defaultValue;

    if (isHoldout()) return defaultValue;

    return gb.getFeatureValue(featureKey, defaultValue);
  }

  /* ---------------------------------------------------------
   * SEGMENTATION HELPERS
   * --------------------------------------------------------- */

  /**
   * Indica si el usuario actual es parte del grupo de control global (Holdout)
   */
  function isHoldout() {
    const gb = getGrowthBook();
    return gb ? gb.isOn(HOLDOUT_KEY) : false;
  }

  /**
   * Indica si el usuario es elegible para ver experimentos y mejoras
   */
  function isExposed() {
    return !isHoldout();
  }

  /**
   * Ejecuta un experimento directo (sin feature flag) y maneja el Holdout automáticamente.
   *
   * @param {Object} experiment - Configuración del experimento.
   * @param {string} experiment.key - Identificador único del experimento en GrowthBook.
   * @param {string[]} experiment.variations - Array de nombres para las variantes (ej: ["control", "v1"]).
   *
   * @returns {Object} Un objeto con la propiedad .value (el nombre de la variante elegida).
   *                   Si el usuario es Holdout, .value siempre será la primera variación (index 0).
   */
  function run(experiment) {
    const gb = getGrowthBook();
    if (!gb) return { value: experiment.variations[0] };

    if (isHoldout()) {
      return { value: experiment.variations[0], inExperiment: false };
    }

    // 1. Delegar la evaluación a la máquina de estados del SDK
    const result = gb.run(experiment);

    // 2. Detectar explícitamente si hay un override de QA activo para este experimento

    // A. Forzado por parámetro en la URL
    const isUrlForced = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has(experiment.key);

    // B. Forzado desde la pestaña "Experiments" de GrowthBook DevTools
    // Buscamos el mapa de variaciones forzadas de forma segura dentro del contexto interno del SDK
    const forcedVariationsMap = (typeof gb.getForcedVariations === 'function' ? gb.getForcedVariations() : null) || gb.context?.forcedVariations || gb._context?.forcedVariations || {};
    const isExperimentOverridden = forcedVariationsMap[experiment.key]!== undefined;

    const isQAOverride = isUrlForced || isExperimentOverridden;

    // 3. Verificar estado del experimento en el payload del servidor
    // Cubre el caso donde GrowthBook self-hosted no aplica el override automáticamente
    const payloadOverrides = gb.context?.experiments || gb._context?.experiments || [];
    const payloadOverride = payloadOverrides.find?.(e => e.key === experiment.key);
    const isInactiveInPayload = payloadOverride?.status === 'stopped' || payloadOverride?.status === 'draft';

    if (!isQAOverride && (!result.inExperiment || isInactiveInPayload)) {
      return { value: experiment.variations[0], inExperiment: false };
    }

    // 4. Se retorna el resultado (nativamente inyectado con el preview de QA o el valor orgánico)
    return result;
  }

  /* ---------------------------------------------------------
   * REACTIVITY
   * --------------------------------------------------------- */

  function onFeatureChange(featureKey, callback, runImmediately = true) {
    if (!featureCallbacks.has(featureKey)) {
      featureCallbacks.set(featureKey, []);
    }

    featureCallbacks.get(featureKey).push(callback);

    if (runImmediately) {
      callback(getEffectiveFeatureState({ featureKey }));
    }
  }

  function onVariantChange(featureKey, callback, runImmediately = true) {
    if (!variantCallbacks.has(featureKey)) {
      variantCallbacks.set(featureKey, []);
    }

    variantCallbacks.get(featureKey).push(callback);

    if (runImmediately) {
      callback(getVariant(featureKey));
    }
  }

  /**
   * Escucha cambios entre el estado "exposed" y "holdout".
   * El callback se llama inmediatamente con el estado actual y luego
   * cada vez que el usuario pasa de expose → holdout o viceversa.
   *
   * @param {Function} callback - Recibe { isExposed: boolean, isHoldout: boolean }
   * @param {boolean} runImmediately - Si true (default), ejecuta el callback al registrarse.
   *
   * @example
   * FeatureManager.onExposureChange(({ isExposed, isHoldout }) => {
   *   if (isExposed) showExperiments();
   *   else hideExperiments();
   * });
   */
  function onExposureChange(callback, runImmediately = true) {
    exposureCallbacks.push(callback);

    if (runImmediately) {
      const holdout = isHoldout();
      callback({ isExposed: !holdout, isHoldout: holdout });
    }
  }

  /**
   * Versión reactiva de `run()`. Ejecuta el experimento inmediatamente
   * y llama al callback cada vez que GrowthBook cambia la variante asignada.
   * Respeta el Holdout: si el usuario es Holdout, el callback recibe la
   * primera variación (index 0) como en `run()`.
   *
   * @param {Object}   experiment            - Misma configuración que `FeatureManager.run()`.
   * @param {string}   experiment.key        - Clave del experimento en GrowthBook.
   * @param {string[]} experiment.variations - Array de variantes (ej: ["control", "v1"]).
   * @param {Function} callback              - Recibe la variante activa (string).
   * @param {boolean}  runImmediately        - Si true (default), ejecuta el callback al registrarse.
   *
   * @example
   * FeatureManager.onRunChange(
   *   { key: "test-pdp-layout", variations: ["control", "v1", "v2"] },
   *   (variant) => {
   *     if (variant === "v1") applyV1();
   *     else if (variant === "v2") applyV2();
   *     else applyControl();
   *   }
   * );
   */
  function onRunChange(experiment, callback, runImmediately = true) {
    const { key } = experiment;

    if (!runCallbacks.has(key)) {
      runCallbacks.set(key, { experiment, callbacks: [] });
    }

    runCallbacks.get(key).callbacks.push(callback);

    if (runImmediately) {
      const { value, inExperiment } = run(experiment);
      const finalValue = inExperiment ? value : experiment.variations[0];
      lastRunState.set(key, finalValue);
      callback(finalValue);
    }
  }

  /* ---------------------------------------------------------
   * DOM HELPERS
   * --------------------------------------------------------- */

  function toggleClass(featureKey, selector, className) {
    onFeatureChange(featureKey, (enabled) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.toggle(className, enabled);
      });
    });
  }

  /**
   * Aplica clases basadas en variante
   * Ej: prefix-a, prefix-b, prefix-control
   */
  function applyVariantClass(featureKey, selector, classPrefix = "") {
    onVariantChange(featureKey, (variant) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.forEach((cls) => {
          if (cls.startsWith(classPrefix)) {
            el.classList.remove(cls);
          }
        });

        if (variant) {
          el.classList.add(`${classPrefix}${variant}`);
        }
      });
    });
  }

  /* ---------------------------------------------------------
   * GrowthBook Renderer
   * --------------------------------------------------------- */

  function updateAttributes() {
    const gb = getGrowthBook();
    if (!gb) return;

    const isHoldoutOn = gb.isOn(HOLDOUT_KEY);

    gb.setAttributes({
      ...gb.getAttributes(),
      [HOLDOUT_ATTR]: isHoldoutOn ? HOLDOUT_ON : HOLDOUT_OFF,
    });
  }

  function initRenderer() {
    const gb = getGrowthBook();
    if (!gb?.setRenderer) return;

    // Sync attributes before first render
    updateAttributes();

    gb.setRenderer(() => {
      // Exposure state (expose <-> holdout)
      const currentHoldout = isHoldout();
      if (lastHoldoutState === null || lastHoldoutState !== currentHoldout) {
        lastHoldoutState = currentHoldout;
        exposureCallbacks.forEach((cb) =>
          cb({ isExposed: !currentHoldout, isHoldout: currentHoldout })
        );
      }

      // Boolean features
      featureCallbacks.forEach((callbacks, featureKey) => {
        const enabled = getEffectiveFeatureState({ featureKey });
        callbacks.forEach((cb) => cb(enabled));
      });

      // Variants (feature flags)
      variantCallbacks.forEach((callbacks, featureKey) => {
        const value = getVariant(featureKey);
        callbacks.forEach((cb) => cb(value));
      });

      // Direct experiments (run-based)
      runCallbacks.forEach(({ experiment, callbacks }, key) => {
        const { value, inExperiment } = run(experiment);
        const finalValue = inExperiment ? value : experiment.variations[0];
        const prev = lastRunState.get(key);

        if (prev !== finalValue) {
          lastRunState.set(key, finalValue);
          callbacks.forEach((cb) => cb(finalValue));
        }
      });

      if (!hasRenderedOnce && gb.ready) {
        hasRenderedOnce = true;
        window.dispatchEvent(
          new CustomEvent("featureManager::ready")
        );
      }
    });

    gb.refreshFeatures();
  }

  /* ---------------------------------------------------------
   * Wait for GrowthBook
   * --------------------------------------------------------- */

  function waitForGrowthBook(timeout = 10000) {
    const start = Date.now();
    const interval = setInterval(() => {
      if (getGrowthBook()) {
        clearInterval(interval);
        initRenderer();
        return;
      }
      if (Date.now() - start >= timeout) {
        clearInterval(interval);
      }
    }, 50);
  }

  waitForGrowthBook();

  /* ---------------------------------------------------------
   * PUBLIC API
   * --------------------------------------------------------- */

  return {
    // Config
    initConfig,
    getConfig,
    getLocalValue,

    // Segmentation
    isHoldout,
    isExposed,
    run,

    // Boolean features
    isEnabled,
    getEffectiveFeatureState,
    onFeatureChange,
    toggleClass,

    // Variants
    getVariant,
    onVariantChange,
    applyVariantClass,

    // Exposure tracking
    onExposureChange,

    // Direct experiment reactivity
    onRunChange,
  };
})();

/**
 * Helpers para identificar tipo de página en Shopify
 * Se basan en las clases del body tag
 */

function isHomePage() {
  return document.body.classList.contains('index');
}

function isProductPage() {
  return document.body.classList.contains('product');
}

function isCollectionPage() {
  return document.body.classList.contains('collection');
}

function isSearchPage() {
  return document.body.classList.contains('search');
}

function isPage() {
  return document.body.classList.contains('page');
}

/**
 * Sets the data-is-exposed attribute on the body tag
 * and updates it every time the exposure state changes
 */
window.addEventListener('featureManager::ready', () => {
  document.body.setAttribute("data-is-exposed", FeatureManager.isExposed());
});

FeatureManager.onExposureChange(({ isExposed }) => {
  document.body.setAttribute("data-is-exposed", isExposed);
});
